import _ from 'lodash';
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMutation, useQuery } from '@apollo/client';
import { StripePaymentMethods, ErrorHandler } from '@playbookux/playbook-web-core';
import { Loader } from 'semantic-ui-react';
import {
  Button,
  Content,
  Header,
  Input,
  InputContainer,
  Modal,
  StripeContainer,
  Text,
  ContainerInputError,
  ErrorMsg,
  LabelValueRow,
  Label,
  Value,
} from './styles';
import { config } from '../../../../../config';

import {
  VERIFY_CAPTCHA,
  SAVED_CARDS,
} from '../../graphql';
import { GET_UPLOAD_PRICE } from './graphql';

const HEADER = 'Buy more storage';
const INPUT_MESSAGE = 'How much storage would you like to purchase?';

const schema = yup
  .number()
  .integer('Storage amount must be an integer')
  .positive('Enter a valid number')
  .typeError('Enter a valid number')
  .required('Required');

const BuyStorageModal = (props) => {
  const {
    callback,
    error,
    onClose,
    onSubmit,
    open,
    loading,
    companyId,
    token,
  } = props;

  const { data: { paymentMethods } = {}, loading: isLoadingCards } = useQuery(SAVED_CARDS, {
    variables: { companyId },
    skip: !companyId,
  });

  const { data: { productPrice } = {}, loading: isLoadingPrice } = useQuery(GET_UPLOAD_PRICE, {
    variables: {
      companyId,
      productType: 'upload',
    },
    skip: !companyId,
  });

  const defaultPaymentMethod = useMemo(() => {
    if (_.isEmpty(paymentMethods)) return null;

    const { id } = _.find(paymentMethods, 'isDefault') || _.first(paymentMethods);

    return id;
  }, [paymentMethods]);

  const isRecaptchaEnabled = config.REACT_APP_RECAPTCHA_ENABLED === 'true';
  const recaptchaRef = useRef();

  const [storageAmount, setStorageAmount] = useState(1);
  const [inputError, setInputError] = useState();
  const [selectedCard, setSelectedCard] = useState(defaultPaymentMethod);
  const [rememberCard, setRememberCard] = useState(false);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(!isRecaptchaEnabled);

  const [verifyCaptcha] = useMutation(VERIFY_CAPTCHA);

  useEffect(() => {
    if (!open) {
      setStorageAmount(1);
    }
  }, [open]);

  const handleChange = (e, { value }) => {
    setInputError(null);
    const parsedValue = parseInt(value, 10);
    setStorageAmount(Number.isNaN(parsedValue) ? '' : parsedValue);

    schema.validate(parsedValue)
      .catch((err) => {
        setInputError(err.message);
      });
  };

  const resetStates = () => {
    setIsCaptchaVerified(false);
    setInputError(undefined);
    setStorageAmount(1);
  };

  const handleSubmit = () => {
    if (inputError) return;
    if (!isCaptchaVerified && isRecaptchaEnabled) {
      setInputError('Something wrong with card or captcha, try again');
      return;
    }
    if (!selectedCard && !token) {
      setInputError('Please select a payment method or enter a new card');
      return;
    }

    schema.validate(storageAmount)
      .then(() => {
        onSubmit(storageAmount, rememberCard, selectedCard);
        resetStates();
      })
      .catch((err) => {
        setInputError(err.message);
      });
  };

  const handleCaptchaVerify = async (responsetoken) => {
    try {
      const { data } = await verifyCaptcha({
        variables: {
          token: responsetoken,
        },
      });

      const { verifyCaptcha: { success } } = data;

      if (!success) {
        setInputError('Something wrong with captcha, try again');
        throw new Error('Something wrong with captcha, try again');
      }
      setIsCaptchaVerified(true);
    } catch (e) {
      setInputError('Something wrong with captcha, try again');
      recaptchaRef.current.reset();
    }
  };

  const handleClose = () => {
    resetStates();
    onClose(!open);
  };

  if (isLoadingCards || isLoadingPrice) {
    return (
      <Loader data-loader="billing-loader" />
    );
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
    >
      <Content>
        <Header as="h3">{HEADER}</Header>

        <InputContainer>
          <Text withContainer data-label-title>{INPUT_MESSAGE}</Text>
          <Input
            data-input-storage-amount
            type="number"
            min="1"
            step="1"
            onChange={handleChange}
            value={storageAmount}
            error={inputError}
          />
          <Text noMargin>{' GB '}</Text>
        </InputContainer>
        <ContainerInputError>
          {inputError && (
            <ErrorMsg>{inputError}</ErrorMsg>
          )}
        </ContainerInputError>
        <LabelValueRow>
          <Label data-label-storage-cost>Cost per GB:</Label>
          <Value data-storage-cost>
            $
            {productPrice.price}
          </Value>
        </LabelValueRow>

        <LabelValueRow bold>
          <Label data-label-storage-total>Total cost:</Label>
          <Value data-storage-total>
            $
            {(storageAmount * productPrice.price)}
          </Value>
        </LabelValueRow>

        <StripeContainer>
          <StripePaymentMethods
            callback={callback}
            onSetRememberCard={setRememberCard}
            onSetSelectedCard={setSelectedCard}
            paymentMethods={paymentMethods}
            rememberCard={rememberCard}
            selectedCard={selectedCard}
          />
        </StripeContainer>

        {error && (
          <ErrorHandler
            error={error}
            defaultMessage="Error while purchasing storage."
          />
        )}

        {isRecaptchaEnabled && (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={config.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY_V2}
            onChange={handleCaptchaVerify}
          />
        )}

        <Button
          data-submit-payment
          disabled={(!isCaptchaVerified && isRecaptchaEnabled) || !storageAmount || inputError || (!selectedCard && !token)}
          loading={loading}
          onClick={handleSubmit}
        >
          Submit & Pay
        </Button>
      </Content>
    </Modal>
  );
};

BuyStorageModal.propTypes = {
  companyId: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  callback: PropTypes.func.isRequired,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
};

BuyStorageModal.defaultProps = {
  error: undefined,
  loading: false,
};

export default BuyStorageModal;
